import axios from 'axios';
import { auth } from '../../../firebase';
import { cleanJsonEmptyValues } from '../../../utils/formatUtils';
import { AccessWithDetails } from '../../Interfaces/admin/Role.interfaces';
import {
  AccessType,
  ApprovalSequence,
  ApprovalSequenceMember,
  ApprovalSequenceSummary,
} from '../../Interfaces/admin/ApprovalSequence.interface';

const apiUrl = process.env['NX_API_URL'];

export const getApprovalSequence = async (
  id: string,
  signal?: AbortSignal
): Promise<ApprovalSequence> => {
  const response = await axios.get(`${apiUrl}/api/approval_sequence/${id}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export type getApprovalSequencesArgs = {
  page: number;
  amountPerPage?: number;
  companies?: string[];
  report?: string;
};

export const getApprovalSequences = async (
  params: getApprovalSequencesArgs,
  signal?: AbortSignal
): Promise<{
  sequences: ApprovalSequenceSummary[];
  total: number;
}> => {
  const cleanedParams = cleanJsonEmptyValues(params);

  const response = await axios.get(`${apiUrl}/api/approval_sequence`, {
    signal,
    params: cleanedParams,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const getApprovalOrderSections = async (
  signal?: AbortSignal
): Promise<string[]> => {
  const response = await axios.get(
    `${apiUrl}/api/roles/sections/approval-order`,
    {
      signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const getApprovalSequencesCopyMembers = async (
  id: string,
  params: { companyID: string; report: string },
  signal?: AbortSignal
): Promise<Omit<ApprovalSequenceMember, 'id'>[]> => {
  const response = await axios.get(
    `${apiUrl}/api/approval_sequence/${id}/copy`,
    {
      signal,
      params,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const getAccess = async (
  filters: {
    companyID?: string;
    type?: AccessType[];
    report?: string;
    enabled?: boolean;
    shouldApprove?: boolean;
  },
  signal?: AbortSignal
): Promise<AccessWithDetails[]> => {
  const cleanedFilters = cleanJsonEmptyValues(filters);
  const response = await axios.get(`${apiUrl}/api/access`, {
    signal,
    params: cleanedFilters,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response.data;
};

type SequenceMembers = { step: number; access: string; assigneeId?: string };

export type PostApprovalSequenceArgs = {
  companyID: string;
  report: string;
  members: SequenceMembers[];
};

export const postApprovalSequence = async (data: PostApprovalSequenceArgs) => {
  const cleanedData = cleanJsonEmptyValues(data);

  const response = await axios.post(
    `${apiUrl}/api/approval_sequence`,
    cleanedData,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const putApprovalSequence = async (
  id: string,
  data: PostApprovalSequenceArgs
) => {
  const cleanedData = cleanJsonEmptyValues(data);

  const response = await axios.put(
    `${apiUrl}/api/approval_sequence/${id}`,
    cleanedData,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const deleteApprovalSequence = async (id: string) => {
  const response = await axios.delete(`${apiUrl}/api/approval_sequence/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const result = response.data;
  return result;
};
